/**
 * Events API service for managing calendar events.
 * @module lib/api/events.api
 */

import {
  PaginationParams,
  PaginatedResponse,
  CreateEvent,
  UpdateEvent,
  Event,
  EventAgendaItem,
  EventDay,
  EventModuleLink,
  EventHost,
  EventInvitee,
  EventRsvp,
  EventSession,
  EventSessionHost,
  EventSessionSpeaker,
  EventSpeaker,
  FindEventByExternalSourceInput,
  CreateEventAgendaItem,
  CreateEventDay,
  PromoteExternalCalendarEventInput,
  UpdateEventAgendaItem,
  CreateEventSession,
  UpdateEventSession,
  CreateEventSpeaker,
  UpdateEventSpeaker,
  CreateEventHost,
  UpdateEventHost,
  CreateEventSessionSpeaker,
  CreateEventSessionHost,
  UpdateEventDay,
  UpdateEventModuleLinksInput,
} from '@scrambl/shared';
import { BaseApi } from './base-api';
import { createSingletonApi } from './singleton-factory';
import { buildUpdatePayload } from '@/utils/saveCustomField';

class EventApiService extends BaseApi<Event, CreateEvent, UpdateEvent> {
  constructor() {
    super({
      baseEndpoint: '/events',
      responseNormalization: true
    });
  }

  // Override create to add default fields required by backend
  async create(data: CreateEvent): Promise<Event> {
    const eventData = {
      ...data,
      deletedAt: null,
      creatorId: "", // Will be assigned by backend
      latitude: 0,   // Default value
      longitude: 0,  // Default value
    };

    return super.create(eventData as CreateEvent);
  }

  // Update a single field of an event (PATCH)
  async updateEventField(eventId: string, field: string, value: unknown): Promise<Event> {
    const updateData = buildUpdatePayload(field, value);
    return this.makeRequest<Event>(`${this.config.baseEndpoint}/${eventId}`, {
      method: 'PATCH',
      body: updateData
    });
  }

  // Alias methods for backward compatibility
  async getEvents(params: Partial<PaginationParams> = {}): Promise<PaginatedResponse<Event>> {
    return this.list(params);
  }

  async getEvent(id: string): Promise<Event> {
    return this.findById(id);
  }

  async createEvent(data: CreateEvent): Promise<Event> {
    return this.create(data);
  }

  async updateEvent(id: string, data: UpdateEvent): Promise<Event> {
    return this.update(id, data);
  }

  async deleteEvent(id: string): Promise<void> {
    return this.delete(id);
  }

  async bulkDeleteEvents(ids: string[]): Promise<{ deletedCount: number }> {
    return this.makeRequest<{ deletedCount: number }>(`${this.config.baseEndpoint}/bulk-delete`, {
      method: 'POST',
      body: { ids }
    });
  }

  async checkSlugAvailability(
    slug: string,
    excludeId?: string
  ): Promise<{ available: boolean; reason?: string }> {
    const queryString = excludeId ? `?excludeId=${excludeId}` : '';
    return this.makeRequest<{ available: boolean; reason?: string }>(
      `${this.config.baseEndpoint}/check-slug/${slug}${queryString}`
    );
  }

  async getInvitees(eventId: string): Promise<EventInvitee[]> {
    return this.makeRequest<EventInvitee[]>(`${this.config.baseEndpoint}/${eventId}/invitees`);
  }

  async addInvitees(eventId: string, invitees: Array<Partial<EventInvitee>>): Promise<EventInvitee[]> {
    return this.makeRequest<EventInvitee[]>(`${this.config.baseEndpoint}/${eventId}/invitees`, {
      method: 'POST',
      body: { invitees }
    });
  }

  async lookupExternalEvent(data: FindEventByExternalSourceInput): Promise<Event | null> {
    return this.makeRequest<Event | null>(`${this.config.baseEndpoint}/lookup-external`, {
      method: 'POST',
      body: data,
    });
  }

  async promoteExternalEvent(data: PromoteExternalCalendarEventInput): Promise<Event> {
    return this.makeRequest<Event>(`${this.config.baseEndpoint}/promote-external`, {
      method: 'POST',
      body: data,
    });
  }

  async getEventModuleLinks(eventId: string): Promise<EventModuleLink[]> {
    return this.makeRequest<EventModuleLink[]>(`${this.config.baseEndpoint}/${eventId}/links`);
  }

  async updateEventModuleLinks(
    eventId: string,
    data: UpdateEventModuleLinksInput,
  ): Promise<Event> {
    return this.makeRequest<Event>(`${this.config.baseEndpoint}/${eventId}/links`, {
      method: 'PUT',
      body: data,
    });
  }

  async getRsvps(eventId: string): Promise<EventRsvp[]> {
    return this.makeRequest<EventRsvp[]>(`${this.config.baseEndpoint}/${eventId}/rsvps`);
  }

  // Event days
  async getDays(eventId: string): Promise<EventDay[]> {
    return this.makeRequest<EventDay[]>(`${this.config.baseEndpoint}/${eventId}/days`);
  }

  async createDay(eventId: string, data: CreateEventDay): Promise<EventDay> {
    return this.makeRequest<EventDay>(`${this.config.baseEndpoint}/${eventId}/days`, {
      method: 'POST',
      body: data,
    });
  }

  async updateDay(eventId: string, dayId: string, data: UpdateEventDay): Promise<EventDay> {
    return this.makeRequest<EventDay>(`${this.config.baseEndpoint}/${eventId}/days/${dayId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deleteDay(eventId: string, dayId: string): Promise<void> {
    await this.makeRequest<void>(`${this.config.baseEndpoint}/${eventId}/days/${dayId}`, {
      method: 'DELETE',
    });
  }

  // Sessions
  async getSessions(eventId: string): Promise<EventSession[]> {
    return this.makeRequest<EventSession[]>(`${this.config.baseEndpoint}/${eventId}/sessions`);
  }

  async createSession(eventId: string, data: CreateEventSession): Promise<EventSession> {
    return this.makeRequest<EventSession>(`${this.config.baseEndpoint}/${eventId}/sessions`, {
      method: 'POST',
      body: data,
    });
  }

  async updateSession(eventId: string, sessionId: string, data: UpdateEventSession): Promise<EventSession> {
    return this.makeRequest<EventSession>(`${this.config.baseEndpoint}/${eventId}/sessions/${sessionId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deleteSession(eventId: string, sessionId: string): Promise<void> {
    await this.makeRequest<void>(`${this.config.baseEndpoint}/${eventId}/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  async getSessionSpeakers(eventId: string, sessionId: string): Promise<EventSessionSpeaker[]> {
    return this.makeRequest<EventSessionSpeaker[]>(`${this.config.baseEndpoint}/${eventId}/sessions/${sessionId}/speakers`);
  }

  async addSessionSpeakers(
    eventId: string,
    sessionId: string,
    speakers: CreateEventSessionSpeaker[]
  ): Promise<EventSessionSpeaker[]> {
    return this.makeRequest<EventSessionSpeaker[]>(
      `${this.config.baseEndpoint}/${eventId}/sessions/${sessionId}/speakers`,
      {
        method: 'POST',
        body: { speakers },
      }
    );
  }

  async removeSessionSpeaker(eventId: string, sessionId: string, speakerId: string): Promise<void> {
    await this.makeRequest<void>(`${this.config.baseEndpoint}/${eventId}/sessions/${sessionId}/speakers/${speakerId}`, {
      method: 'DELETE',
    });
  }

  async getSessionHosts(eventId: string, sessionId: string): Promise<EventSessionHost[]> {
    return this.makeRequest<EventSessionHost[]>(`${this.config.baseEndpoint}/${eventId}/sessions/${sessionId}/hosts`);
  }

  async addSessionHosts(
    eventId: string,
    sessionId: string,
    hosts: CreateEventSessionHost[]
  ): Promise<EventSessionHost[]> {
    return this.makeRequest<EventSessionHost[]>(
      `${this.config.baseEndpoint}/${eventId}/sessions/${sessionId}/hosts`,
      {
        method: 'POST',
        body: { hosts },
      }
    );
  }

  async removeSessionHost(eventId: string, sessionId: string, hostId: string): Promise<void> {
    await this.makeRequest<void>(`${this.config.baseEndpoint}/${eventId}/sessions/${sessionId}/hosts/${hostId}`, {
      method: 'DELETE',
    });
  }

  // Agenda
  async getAgendaItems(eventId: string): Promise<EventAgendaItem[]> {
    return this.makeRequest<EventAgendaItem[]>(`${this.config.baseEndpoint}/${eventId}/agenda`);
  }

  async createAgendaItem(eventId: string, data: CreateEventAgendaItem): Promise<EventAgendaItem> {
    return this.makeRequest<EventAgendaItem>(`${this.config.baseEndpoint}/${eventId}/agenda`, {
      method: 'POST',
      body: data,
    });
  }

  async updateAgendaItem(eventId: string, agendaItemId: string, data: UpdateEventAgendaItem): Promise<EventAgendaItem> {
    return this.makeRequest<EventAgendaItem>(`${this.config.baseEndpoint}/${eventId}/agenda/${agendaItemId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deleteAgendaItem(eventId: string, agendaItemId: string): Promise<void> {
    await this.makeRequest<void>(`${this.config.baseEndpoint}/${eventId}/agenda/${agendaItemId}`, {
      method: 'DELETE',
    });
  }

  // Speakers
  async getSpeakers(eventId: string): Promise<EventSpeaker[]> {
    return this.makeRequest<EventSpeaker[]>(`${this.config.baseEndpoint}/${eventId}/speakers`);
  }

  async createSpeaker(eventId: string, data: CreateEventSpeaker): Promise<EventSpeaker> {
    return this.makeRequest<EventSpeaker>(`${this.config.baseEndpoint}/${eventId}/speakers`, {
      method: 'POST',
      body: data,
    });
  }

  async updateSpeaker(eventId: string, speakerId: string, data: UpdateEventSpeaker): Promise<EventSpeaker> {
    return this.makeRequest<EventSpeaker>(`${this.config.baseEndpoint}/${eventId}/speakers/${speakerId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deleteSpeaker(eventId: string, speakerId: string): Promise<void> {
    await this.makeRequest<void>(`${this.config.baseEndpoint}/${eventId}/speakers/${speakerId}`, {
      method: 'DELETE',
    });
  }

  // Hosts
  async getHosts(eventId: string): Promise<EventHost[]> {
    return this.makeRequest<EventHost[]>(`${this.config.baseEndpoint}/${eventId}/hosts`);
  }

  async createHost(eventId: string, data: CreateEventHost): Promise<EventHost> {
    return this.makeRequest<EventHost>(`${this.config.baseEndpoint}/${eventId}/hosts`, {
      method: 'POST',
      body: data,
    });
  }

  async updateHost(eventId: string, hostId: string, data: UpdateEventHost): Promise<EventHost> {
    return this.makeRequest<EventHost>(`${this.config.baseEndpoint}/${eventId}/hosts/${hostId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deleteHost(eventId: string, hostId: string): Promise<void> {
    await this.makeRequest<void>(`${this.config.baseEndpoint}/${eventId}/hosts/${hostId}`, {
      method: 'DELETE',
    });
  }

  // Images
  async uploadEventBanner(eventId: string, file: File): Promise<{ bannerImageUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.makeRequest<{ bannerImageUrl: string }>(`${this.config.baseEndpoint}/${eventId}/banner`, {
      method: 'POST',
      body: formData,
    });
  }

  async getEventBanner(eventId: string): Promise<{ bannerImageUrl: string | null }> {
    return this.makeRequest<{ bannerImageUrl: string | null }>(`${this.config.baseEndpoint}/${eventId}/banner`);
  }

  async removeEventBanner(eventId: string): Promise<Event> {
    return this.makeRequest<Event>(`${this.config.baseEndpoint}/${eventId}/banner`, {
      method: 'DELETE',
    });
  }

  async uploadSpeakerImage(eventId: string, speakerId: string, file: File): Promise<{ imageUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.makeRequest<{ imageUrl: string }>(
      `${this.config.baseEndpoint}/${eventId}/speakers/${speakerId}/image`,
      {
        method: 'POST',
        body: formData,
      }
    );
  }

  async getSpeakerImage(eventId: string, speakerId: string): Promise<{ imageUrl: string | null }> {
    return this.makeRequest<{ imageUrl: string | null }>(
      `${this.config.baseEndpoint}/${eventId}/speakers/${speakerId}/image`
    );
  }

  async removeSpeakerImage(eventId: string, speakerId: string): Promise<EventSpeaker> {
    return this.makeRequest<EventSpeaker>(
      `${this.config.baseEndpoint}/${eventId}/speakers/${speakerId}/image`,
      {
        method: 'DELETE',
      }
    );
  }

  async uploadAgendaImage(eventId: string, agendaItemId: string, file: File): Promise<{ imageUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.makeRequest<{ imageUrl: string }>(
      `${this.config.baseEndpoint}/${eventId}/agenda/${agendaItemId}/image`,
      {
        method: 'POST',
        body: formData,
      }
    );
  }

  async getAgendaImage(eventId: string, agendaItemId: string): Promise<{ imageUrl: string | null }> {
    return this.makeRequest<{ imageUrl: string | null }>(
      `${this.config.baseEndpoint}/${eventId}/agenda/${agendaItemId}/image`
    );
  }

  async removeAgendaImage(eventId: string, agendaItemId: string): Promise<EventAgendaItem> {
    return this.makeRequest<EventAgendaItem>(
      `${this.config.baseEndpoint}/${eventId}/agenda/${agendaItemId}/image`,
      {
        method: 'DELETE',
      }
    );
  }

  // Fix typo in original API - keep old name for compatibility but add correct name
  async udpateEventField(eventId: string, field: string, value: unknown): Promise<Event> {
    return this.updateEventField(eventId, field, value);
  }
}

// Export singleton instance using factory
export const eventApi = createSingletonApi(EventApiService);

// Export for backward compatibility
export { eventApi as EventApi };
